import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Button, Divider, Grid, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useState } from 'react';
import moment from 'moment';
import { AccountData, DashboardService } from '../../api';
import { ExtendedDashboardBox } from './ExtendedDashboardBox';
import { accountHasEventOrderPages, accountHasPrivateOrderPages } from '../../common/AccountUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';

export const extendedDashboardFetcher = async () => DashboardService.extendedDashboard();

export const ExtendedDashboard = () => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);

  const [lastFetch, setLastFetch] = useState<moment.Moment>(moment());
  const [isRefetching, setIsRefetching] = useState(false);

  const {
    data,
    isLoading: swrLoading,
    mutate: refetch,
  } = useSWR({ opId: 'getExtendedDashboard' }, extendedDashboardFetcher, {
    revalidateOnFocus: false,
    refreshInterval: 0,
  });
  const handleRefresh = async () => {
    setIsRefetching(true);

    await refetch();
    setLastFetch(moment());
    setIsRefetching(false);
  };
  const hasEventPages = accountHasEventOrderPages(account);
  const hasPrivateOrderPages = accountHasPrivateOrderPages(account);
  const isLoading = swrLoading || isRefetching;
  return (
    <Grid container gap={3} p={2} overflow="auto">
      <Grid container flexDirection="column">
        <Typography variant="h5">{t('dashboard.extendedtitle')}</Typography>
        <Typography variant="h6" color={'gray'}>
          {t('dashboard.extendedsubtitle')}
        </Typography>
        <Grid container alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.lastUpdate')}:{' '}
            {`${lastFetch.format(longDateFormat)} ${lastFetch.format(getTimeFormat(account))}`}
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isLoading} size="small">
            {t('dashboard.refresh')}
          </Button>
        </Grid>
      </Grid>
      <Grid container gap={2}>
        <Typography variant="h6">{t('dashboard.summary')}</Typography>
        <Grid container gap={2}>
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.totalIncome')}
            today={data?.today.total_income}
            yesterday={data?.yesterday.total_income}
            month={data?.fromBeginningOfMonth.total_income}
            isCurrency
            emphasized
          />
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.totalOrders')}
            today={(data?.today.private_orders || 0) + (data?.today.event_orders || 0) + (data?.today.vouchers || 0)}
            yesterday={(data?.yesterday.private_orders || 0) + (data?.yesterday.event_orders || 0)}
            month={(data?.fromBeginningOfMonth.private_orders || 0) + (data?.fromBeginningOfMonth.event_orders || 0)}
          />
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.totalPersons')}
            today={(data?.today.total_persons_event || 0) + (data?.today.total_persons_private || 0)}
            yesterday={(data?.yesterday.total_persons_event || 0) + (data?.yesterday.total_persons_private || 0)}
            month={
              (data?.fromBeginningOfMonth.total_persons_event || 0) +
              (data?.fromBeginningOfMonth.total_persons_private || 0)
            }
          />
        </Grid>
      </Grid>
      <Divider sx={{ width: '100%' }} />
      <Grid container gap={2}>
        <Typography variant="h6">{t('dashboard.orders')}</Typography>
        <Grid container gap={2}>
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.totalIncomeWorkshops')}
            today={data?.today.income_orders}
            yesterday={data?.yesterday.income_orders}
            month={data?.fromBeginningOfMonth.income_orders}
            isCurrency
          />
          {hasEventPages && (
            <ExtendedDashboardBox
              isLoading={isLoading}
              title={t('dashboard.ticketsForEvents')}
              today={data?.today.event_orders}
              yesterday={data?.yesterday.event_orders}
              month={data?.fromBeginningOfMonth.event_orders}
            />
          )}
          {hasPrivateOrderPages && (
            <ExtendedDashboardBox
              isLoading={isLoading}
              title={t('dashboard.ticketsForPrivateOrders')}
              today={data?.today.private_orders}
              yesterday={data?.yesterday.private_orders}
              month={data?.fromBeginningOfMonth.private_orders}
            />
          )}
        </Grid>

        <Divider sx={{ width: '100%' }} />

        <Typography variant="h6">{t('dashboard.vouchers')}</Typography>
        <Grid container gap={2}>
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.voucherIncome')}
            today={data?.today.income_vouchers}
            yesterday={data?.yesterday.income_vouchers}
            month={data?.fromBeginningOfMonth.income_vouchers}
            isCurrency
          />
          <ExtendedDashboardBox
            isLoading={isLoading}
            title={t('dashboard.vouchersSold')}
            today={data?.today.vouchers}
            yesterday={data?.yesterday.vouchers}
            month={data?.fromBeginningOfMonth.vouchers}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
